import { useMemo, useState } from "react";
import { useBlocker } from "react-router-dom";
import { useGlobalReducer, useGlobalState } from "../../../GlobalContext";
import { administratorRoles, userHasCorrectRole } from "../../config/roles";
import {
  addFailureNotification,
  addSuccessNotification,
} from "../../shell/notification/notifications";
import {
  getSavedViewPayload,
  readSavedView,
  writeSavedView,
} from "./savedViewService";
import { UseSavedViewsOptions } from "./useSavedViews.types";

interface ActiveView {
  id: string;
  name: string;
  /** The view's payload when it was loaded or last saved, to spot changes against. */
  baseline: string;
}

/**
 * The saved view a search was loaded from or saved as, and whether the search has
 * been changed since. Leaving the page with changes is blocked until the user chooses.
 */
export function useSavedViews({ search, allowedEntityTypes }: UseSavedViewsOptions) {
  const globalDispatch = useGlobalReducer();
  const { user } = useGlobalState();
  const [view, setView] = useState<ActiveView | null>(null);
  const canManage = userHasCorrectRole(user, administratorRoles);

  const payload = useMemo(
    () => JSON.stringify(getSavedViewPayload(search.query)),
    [search.query]
  );
  const hasUnsavedChanges = view !== null && payload !== view.baseline;

  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      hasUnsavedChanges && currentLocation.pathname !== nextLocation.pathname
  );

  /** Replaces the search with the view's. False if it could not be read. */
  const load = async (id: string) => {
    const loaded = await readSavedView(globalDispatch, id, allowedEntityTypes);

    if (!loaded) {
      return false;
    }

    search.loadQuery(loaded.query);
    setView({
      ...loaded.view,
      baseline: JSON.stringify(getSavedViewPayload(loaded.query)),
    });

    if (loaded.unresolved.length > 0) {
      // The view returns more records than it was saved with, so say so.
      addFailureNotification(
        globalDispatch,
        `View ${loaded.view.name} could not be fully loaded. These filters are no longer available and have been removed: ${loaded.unresolved.join(", ")}`
      );
    }

    return true;
  };

  /** Saves the search as a new view, which becomes the current one. */
  const saveAs = async (name: string) => {
    const id = await writeSavedView(globalDispatch, search.query, name);

    if (!id) {
      return false;
    }

    addSuccessNotification(globalDispatch, `Saved view ${name} created`);
    setView({ id, name, baseline: payload });

    return true;
  };

  /** Saves the search over the current view. */
  const update = async () => {
    if (!view) {
      return false;
    }

    const id = await writeSavedView(globalDispatch, search.query, view.name, view.id);

    if (!id) {
      return false;
    }

    addSuccessNotification(globalDispatch, `Saved view ${view.name} updated`);
    setView({ ...view, baseline: payload });

    return true;
  };

  const clear = () => setView(null);

  return {
    view: view ? { id: view.id, name: view.name } : null,
    canManage,
    hasUnsavedChanges,
    blocker,
    load,
    saveAs,
    update,
    clear,
  };
}

export type SavedViews = ReturnType<typeof useSavedViews>;
